import {
  BatteryPowerType,
  ChartContainer,
  CSVDownloadButton,
  OptimizationGroupMetaInfo,
} from '@e2m-customer-portal/domains';
import { MarketLocation, MarketLocationProvider, useLocale, useStartOfWeek } from '@e2m-customer-portal/hooks';
import { DateTimePicker, Loading, PageTitle, Toolbar } from '@e2m-customer-portal/ui';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { addDays, endOfToday, startOfToday, startOfYear, subDays, subYears, toDate } from 'date-fns';
import { ForecastProperty, Interval, MachineTechnology } from 'generated/graphql';
import { createContext, ReactElement, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mostCompactName } from 'utils/most-compact-name';
import { ChartColor } from './feed-in-chart/utils';

const COLOR_SCHEME_KEY = 'chart-color-variant';

export const ChartColorContext = createContext(0);

interface CoLocationOverviewPage {
  marketLocation: MarketLocation;
  currentForecastProperty?: ForecastProperty;
}

export function CoLocationOverviewPage({
  marketLocation,
  currentForecastProperty,
}: Readonly<CoLocationOverviewPage>): ReactElement<CoLocationOverviewPage> {
  const [t] = useTranslation();
  const locale = useLocale();
  const startOfWeek = useStartOfWeek();
  const [selectedColorVariant, setSelectedColorVariant] = useState(Number(localStorage.getItem(COLOR_SCHEME_KEY) ?? 0));
  const tradingGroupId = marketLocation?.machines[0]?.devices[0]?.tradingGroupId;

  const [dataInterval, setDataInterval] = useState<Interval>({
    start: subDays(startOfToday(), 7),
    end: addDays(endOfToday(), 7),
  });

  const marketLocationName =
    marketLocation.externalName && marketLocation.externalName !== marketLocation.name
      ? marketLocation.externalName
      : mostCompactName(
          marketLocation.externalName || marketLocation.name,
          marketLocation.linkedMarketLocation?.externalName || marketLocation.linkedMarketLocation?.name,
        );
  const allMachines = marketLocation.machines.concat(marketLocation.linkedMarketLocation?.machines ?? []);
  const batteries = allMachines
    .filter((machine) => machine.machineTechnology === MachineTechnology.BATTERY)
    .flatMap((machine) => machine.devices.map((device) => ({ ...device, installedPower: machine.installedPower })));

  const solarDevices = allMachines
    .filter((machine) => machine.machineTechnology === MachineTechnology.SOLAR)
    .flatMap((machine) => machine.devices.map((device) => ({ ...device, installedPower: machine.installedPower })));

  const compositeDevice = allMachines
    .filter((machine) => machine.machineTechnology === MachineTechnology.COMPOSITE)
    .flatMap((machine) => machine.devices)[0];

  const [batteriesPower, setBatteriesPower] = useState<Map<string, BatteryPowerType>>(new Map());

  return (
    <>
      <PageTitle label={t('MASTER_DATA_TAB_PLANT')} title={marketLocationName} />
      <Toolbar
        left={
          <div className="w-full flex items-center justify-start flex-wrap gap-x-4 gap-y-2 *:flex *:flex-col first:*:pl-0 *:pl-4 first:*:border-none last:*:border-none *:border-solid *:border-0 *:border-l *:border-grey-200">
            <div className="w-full md:w-fit">
              <DateTimePicker
                label={t('MARKET_LOCATION_FEED_IN_DATE_PICKER_LABEL')}
                small
                mode="range"
                pickerProps={{
                  dateOnly: true,
                  disabled: [{ before: startOfYear(subYears(new Date(), 1)) }, { after: addDays(endOfToday(), 7) }],
                  locale,
                  startOfWeek,
                  numberOfMonths: 2,
                  fixedWeeks: true,
                  showWeekNumber: true,
                  showOutsideDays: true,
                }}
                required
                range={dataInterval}
                onSelect={({ range }) => setDataInterval({ start: toDate(range.start), end: toDate(range.end) })}
              />
            </div>
            {tradingGroupId && <OptimizationGroupMetaInfo id={tradingGroupId} setBatteriesPower={setBatteriesPower} />}
          </div>
        }
        right={
          <>
            <MarketLocationProvider value={marketLocation}>
              <CSVDownloadButton />
            </MarketLocationProvider>
            <FormControl>
              <InputLabel id="chart-color-scheme">{t('MARKET_LOCATION_FEED_IN_CHART_COLOR_SCHEME')}</InputLabel>
              <Select
                className="h-10"
                value={selectedColorVariant}
                label={t('MARKET_LOCATION_FEED_IN_CHART_COLOR_SCHEME')}
                onChange={(event) => {
                  setSelectedColorVariant(Number(event.target.value));
                  localStorage.setItem(COLOR_SCHEME_KEY, String(event.target.value));
                }}
              >
                {ChartColor.Name.map((variantName, variantIndex) => (
                  <MenuItem key={variantName} value={variantIndex}>
                    <div className="flex items-center gap-2">
                      <div className="h-6 aspect-square rounded-full overflow-hidden">
                        <div className="flex rotate-45 scale-105 *:w-1/3 *:h-6">
                          <div
                            style={{
                              backgroundImage: `linear-gradient(to top right, ${ChartColor.PrimaryFull[variantIndex]}, ${ChartColor.Primary60[variantIndex]})`,
                            }}
                          />
                          <div
                            style={{
                              backgroundImage: `linear-gradient(to top right, ${ChartColor.SecondaryFull[variantIndex]}, ${ChartColor.Secondary60[variantIndex]})`,
                            }}
                          />
                          <div
                            style={{
                              backgroundImage: `linear-gradient(to top right, ${ChartColor.TertiaryFull[variantIndex]}, ${ChartColor.Tertiary60[variantIndex]})`,
                            }}
                          />
                        </div>
                      </div>
                      <div>{variantName}</div>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        }
      />
      <Suspense
        fallback={
          <div className="w-full h-full flex justify-center items-center">
            <Loading />
          </div>
        }
      >
        <ChartColorContext value={selectedColorVariant}>
          <ChartContainer
            marketLocation={marketLocation}
            dataInterval={dataInterval}
            currentForecastProperty={currentForecastProperty}
            batteries={batteries}
            solarDevices={solarDevices}
            compositeDevice={compositeDevice}
            batteriesPower={batteriesPower}
            id={tradingGroupId}
          />
        </ChartColorContext>
      </Suspense>
    </>
  );
}
